import React, { useState, useRef, useEffect, useMemo } from "react";
import moment from "moment";

import { ReactComponent as EditIcon } from "../../assets/images/icons/edit.svg";
import TeamBadge from "../TeamBadge/TeamBadge";

/**
 * FixtureCard - Displays a single fixture/match card
 * Matches Figma design with team crests, scores, and action buttons
 */
const FixtureCard = ({
  fixture,
  result,
  onEdit,
  onDelete,
  onAddResult,
  onEditResult,
  selectedTournament,
  showConflicts = false,
  hideDate = false,
  isReadOnly = false,
  canManageFixtures = true,
  canAddResults = true,
  isLocked = false,
  isOwner = false,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Derive status from result OR fixture
  const getStatus = () => {
    // Check fixture-level completion first (backend sets these)
    if (fixture?.status === "COMPLETED" || fixture?.hasResult === true) {
      return "final";
    }
    // Then check result object (legacy/event-based)
    if (!result?.status) return "pending";
    if (result.status === "Final Score") return "final";
    if (result.status === "Verify Result") return "verify";
    return "pending";
  };


  const status = getStatus();
  const isPending = status === "pending";
  const isFinal = status === "final";
  const isVerify = status === "verify";

  // Get conflict info
  const hasConflict = showConflicts && (fixture?.hasPitchConflict || fixture?.hasEndTimeConflict);
  const hasPitchConflict = fixture?.hasPitchConflict;

  // Get match info. matchTime is a UTC ISO instant; render in viewer-local TZ.
  const matchTime = fixture?.matchTime
    ? moment(fixture.matchTime).format("HH:mm")
    : moment(result?.eventDateTime).format("HH:mm");
  const pitch = fixture?.pitch || result?.pitch || "Pitch 1";
  const phaseName = fixture?.phaseName || result?.phaseName || "Groups";
  const groupName = fixture?.groupName || result?.groupName || "Group A";
  const referees = fixture?.referees || [];
  const refereeCount = referees.length;
  const getRefereeNames = () => {
    return referees.map((r) => {
      return r.name || r.userId?.profile?.fullName || r.userId?.profile?.firstName || "Unknown";
    }).join(", ");
  };

  // Determine if this fixture is in the future. Compares by DATE only —
  // a fixture later today still counts as "today", not future.
  const fixtureStart = fixture?.matchTime
    ? moment(fixture.matchTime)
    : result?.eventDateTime
      ? moment(result.eventDateTime)
      : null;
  const isFuture = fixtureStart?.isValid() ? fixtureStart.isAfter(moment(), 'day') : false;

  // Team info - use fixture scores as primary source (backend saves there)
  const homeTeam = {
    title: result?.homeTeam?.title || result?.homeTeam?.name || fixture?.homeTeam?.title || fixture?.homeTeam?.name || "TBD",
    crest: result?.homeTeam?.crest || fixture?.homeTeam?.crest,
    score: fixture?.homeTeamScore ?? result?.homeTeam?.score ?? result?.homeTeam?.scores ?? 0,
  };

  const awayTeam = {
    title: result?.awayTeam?.title || result?.awayTeam?.name || fixture?.awayTeam?.title || fixture?.awayTeam?.name || "TBD",
    crest: result?.awayTeam?.crest || fixture?.awayTeam?.crest,
    score: fixture?.awayTeamScore ?? result?.awayTeam?.score ?? result?.awayTeam?.scores ?? 0,
  };

  const handleResultAction = () => {
    if (isPending && onAddResult) {
      onAddResult(fixture);
    } else if ((isFinal || isVerify) && onEditResult) {
      onEditResult(fixture, result);
    }
  };

  const showActionButton = useMemo(() => {
      return canAddResults && !(isPending && isFuture) && (!isLocked || isOwner)
  }, [canAddResults, isPending, isFuture, isLocked, isOwner])

  return (
    <div
      className={`
        w-full max-w-[342px] h-auto min-h-[270px] bg-[#000F1F] border border-[#192634] rounded-2xl
        overflow-hidden flex flex-col transition-all duration-200
        ${hasConflict ? (hasPitchConflict ? "border-red-500 border-2" : "border-yellow-500 border-2") : ""}
        hover:border-[#00E7FF]/50
      `}
    >
      {/* Date Header (optional) */}
      {!hideDate && result?.eventDateTime && (
        <div className="py-2 text-center">
          <p className="text-sm text-[#FEFCEB] m-0">
            {moment(result.eventDateTime).format("dddd, MMMM D, YYYY")}
          </p>
        </div>
      )}

      {/* Top Bar - Phase/Group info + Edit menu */}
      <div className="mx-3 mt-3 flex justify-between items-center relative">
        <span className={`text-sm ${hasConflict ? (hasPitchConflict ? "text-red-400" : "text-yellow-400") : "text-app-text-secondary"}`}>
          {phaseName} - {groupName} | {pitch}
          {hasConflict && (hasPitchConflict ? " ⚠️" : " ⏰")}
        </span>

        {/* Edit Menu - Hidden in read-only mode or without manage permissions */}
        {!isReadOnly && canManageFixtures && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-1 hover:bg-white/10 rounded-full transition-colors"
            >
              <EditIcon className="w-5 h-5 text-[#FEFCEB]" />
            </button>

            {isMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-36 bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl z-50 overflow-hidden">
                <button
                  onClick={() => {
                    onEdit?.(fixture);
                    setIsMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 text-[#FEFCEB] hover:bg-[#0D1B29] transition-colors text-sm font-medium border-b border-[#192634]"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    onDelete?.(fixture);
                    setIsMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 text-app-error hover:bg-[#0D1B29] transition-colors text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Card Body - Teams and Score */}
      <div className="flex-1 px-4 flex flex-col border-b border-[#192634]">
        <div className="flex items-center justify-between flex-1">
          {/* Home Team */}
          <div className={`flex flex-col items-center gap-2 flex-1 h-full justify-start ${showActionButton ? "pt-8" : "pt-12"}`}>
            <TeamBadge src={homeTeam.crest} alt={homeTeam.title} className="object-contain" />
            <h4 className="text-[#FEFCEB] text-sm font-bold text-center line-clamp-2">
              {homeTeam.title}
            </h4>
          </div>

          {/* Score / VS */}
          <div className='flex-1 flex justify-center items-center h-full '>

            <div className="flex-1 flex flex-col items-center justify-center">
              {isFinal || isVerify ? (
                <p className={`text-xl font-bold text-[#FEFCEB] ${hasConflict ? (hasPitchConflict ? "text-red-400" : "text-yellow-400") : ""}`}>
                  {homeTeam.score} - {awayTeam.score}
                </p>
              ) : (
                <div className="text-center">
                  <p className="text-[#FEFCEB] text-[12px] font-medium">VS</p>
                  <p className={`text-sm ${hasConflict ? (hasPitchConflict ? "text-red-400" : "text-yellow-400") : "text-app-text"}`}>
                    {matchTime} {hasConflict && (hasPitchConflict ? "⚠️" : "⏰")}
                  </p>
                </div>
              )}
            </div>
          </div>


          {/* Away Team */}
          <div className={`flex flex-col items-center gap-2 flex-1 h-full justify-start ${showActionButton ? "pt-8" : "pt-12"}`}>
            <TeamBadge src={awayTeam.crest} alt={awayTeam.title} className="object-contain" />
            <h4 className="text-[#FEFCEB] text-sm font-bold text-center line-clamp-2">
              {awayTeam.title}
            </h4>
          </div>
        </div>


        {/* Referee Tag */}
        <div className="text-center mb-4 mt-2">
          <span className="text-sm text-app-text-secondary">
            {refereeCount > 0
              ? `Referee: ${getRefereeNames()}`
              : "No referees assigned"}
          </span>
        </div>

        {/* Conflict Warning */}
        {showConflicts && hasConflict && (
          <div className={`mt-2 p-2 rounded text-xs ${hasPitchConflict ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-400"}`}>
            {hasPitchConflict && <p>⚠️ Pitch conflict - Another match scheduled at same time</p>}
            {fixture?.hasEndTimeConflict && <p>⏰ Time conflict - Match exceeds day end time</p>}
          </div>
        )}
      </div>

      {/* Action Button - visible to all roles with canAddResults permission, hidden when phase is locked (unless owner) */}
      {canAddResults && !(isPending && isFuture) && (!isLocked || isOwner) && (
        <button
          onClick={handleResultAction}
          className="h-11 mx-3 my-3 bg-app-card text-[#FEFCEB] rounded-full hover:opacity-50 transition-opacity font-medium text-[15px]"
        >
          {isPending ? "Add result" : "Edit result"}
        </button>
      )}
    </div>
  );
};

export default FixtureCard;
